// Polls /api/inbound/intake/stats and drives FaviconController state.
// Priority: overdue (red) > unread/open (amber) > healthy (green) > idle (unauthenticated).

import { useEffect } from 'react';
import { FaviconController, FaviconState } from '../services/faviconController';

const POLL_INTERVAL_MS = 60_000;
const API_BASE = (import.meta as any).env?.VITE_API_URL ?? '/meos/api';

interface StatsPayload {
  openCount?: number;
  noReplyCount?: number;
  followUpOverdueCount?: number;
}

async function fetchIntakeStats(token: string | null): Promise<StatsPayload> {
  try {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}/inbound/intake/stats`, { headers });
    if (!res.ok) return {};
    return (await res.json()) as StatsPayload;
  } catch {
    return {};
  }
}

function resolveState(stats: StatsPayload, authenticated: boolean): FaviconState {
  if (!authenticated) return { type: 'idle' };

  const overdue = stats.followUpOverdueCount ?? 0;
  const open    = (stats.openCount ?? 0) + (stats.noReplyCount ?? 0);

  if (overdue > 0) return { type: 'overdue', count: overdue };
  if (open > 0)    return { type: 'alert',   count: open };
  return { type: 'healthy' };
}

function getToken(): string | null {
  return localStorage.getItem('ma_jwt') ?? null;
}

export function useFaviconState(isAuthenticated: boolean) {
  useEffect(() => {
    FaviconController.init();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      FaviconController.setState({ type: 'idle' });
      return;
    }

    let cancelled = false;

    const poll = async () => {
      const stats = await fetchIntakeStats(getToken());
      if (cancelled) return;
      FaviconController.setState(resolveState(stats, true));
    };

    poll();
    const timer = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [isAuthenticated]);
}

// Debounced manual trigger — call after any action that changes queue counts (claim, archive, reply).
// Multiple rapid calls collapse into one fetch 500ms after the last call.
let _notifyTimer: ReturnType<typeof setTimeout> | null = null;
export function notifyFaviconCountChange() {
  if (_notifyTimer !== null) clearTimeout(_notifyTimer);
  _notifyTimer = setTimeout(() => {
    _notifyTimer = null;
    const token = getToken();
    fetchIntakeStats(token).then(stats => {
      FaviconController.setState(resolveState(stats, !!token));
    });
  }, 500);
}
